import type { AnalyticsField, AnalyticsMetric, TimeBucket } from '@/shared/analytics/AnalyticsTypes';
import type { SignalContext, SignalData, SignalDatum, SignalValueFormat } from './SignalTypes';

/**
 * Reusable builders for the most common tile `live()` shapes, so individual
 * {@link SignalTileDef}s declare *what* to fetch rather than re-implementing the
 * gateway call, row mapping, and empty-guard each time. Every builder returns a
 * loader compatible with `SignalTileDef.live`; on no data it throws, so the tile
 * keeps its illustrative sample (the established fallback contract).
 */

/** A group-by aggregation rendered as a labelled value series (`bars` / `donut`). */
export interface AggregateSeriesSpec {
    groupBy: AnalyticsField;
    metric: AnalyticsMetric;
    /** Render format for the values; defaults to `count`. */
    format?: SignalValueFormat;
    /** Top-N cap; defaults to 6. */
    limit?: number;
    /** Optional transform for the bucket label (e.g. shorten a user id). */
    label?: (key: string) => string;
    /** Message thrown when the query returns no rows, so the sample is retained. */
    emptyMessage?: string;
}

/** Builds a `live()` loader that group-bys one field and maps each row to a labelled value. */
export function aggregateSeriesLoader(spec: AggregateSeriesSpec): (ctx: SignalContext) => Promise<SignalData> {
    return async ({ gateway, filters }): Promise<SignalData> => {
        const res = await gateway.aggregate({
            source: 'interactions',
            groupBy: [spec.groupBy],
            metrics: [spec.metric],
            orderBy: { metric: spec.metric, direction: 'desc' },
            filters,
            limit: spec.limit ?? 6,
        });
        const labelOf = spec.label ?? ((key: string): string => key);
        const series: SignalDatum[] = res.rows.map((row) => ({
            label: labelOf(row.keys[spec.groupBy] ?? '—'),
            value: row.metrics[spec.metric],
        }));
        if (series.length === 0) throw new Error(spec.emptyMessage ?? 'no data to chart');
        return { series, format: spec.format ?? 'count' };
    };
}

/** A single metric bucketed into successive time periods, rendered as a `trend`. */
export interface TrendSeriesSpec {
    bucket: TimeBucket;
    metric: AnalyticsMetric;
    /** Render format for the values; defaults to `compact`. */
    format?: SignalValueFormat;
    emptyMessage?: string;
}

/** Builds a `live()` loader that time-buckets one metric into a trend series. */
export function trendSeriesLoader(spec: TrendSeriesSpec): (ctx: SignalContext) => Promise<SignalData> {
    return async ({ gateway, filters }): Promise<SignalData> => {
        const res = await gateway.trend({ source: 'interactions', bucket: spec.bucket, metrics: [spec.metric], filters });
        const series: SignalDatum[] = res.points.map((point) => ({ label: point.bucket, value: point.metrics[spec.metric] }));
        if (series.length === 0) throw new Error(spec.emptyMessage ?? 'no trend to chart');
        return { series, format: spec.format ?? 'compact' };
    };
}

/** A per-bucket ratio of two metrics over time (e.g. tokens ÷ turns), rendered as a `trend`. */
export interface TrendRatioSpec {
    bucket: TimeBucket;
    numerator: AnalyticsMetric;
    denominator: AnalyticsMetric;
    /** Render format for the ratio; defaults to `ratio`. */
    format?: SignalValueFormat;
    emptyMessage?: string;
}

/** Builds a `live()` loader whose y-value is a per-bucket ratio of two metrics; buckets with a zero denominator are dropped. */
export function trendRatioLoader(spec: TrendRatioSpec): (ctx: SignalContext) => Promise<SignalData> {
    return async ({ gateway, filters }): Promise<SignalData> => {
        const res = await gateway.trend({ source: 'interactions', bucket: spec.bucket, metrics: [spec.numerator, spec.denominator], filters });
        const series: SignalDatum[] = res.points
            .filter((point) => point.metrics[spec.denominator] > 0)
            .map((point) => ({ label: point.bucket, value: point.metrics[spec.numerator] / point.metrics[spec.denominator] }));
        if (series.length === 0) throw new Error(spec.emptyMessage ?? 'no ratio trend to chart');
        return { series, format: spec.format ?? 'ratio' };
    };
}
